/**
 * Onboarding tour state.
 *
 * Two pieces:
 *   • A per-user "done" flag in localStorage — survives sessions on this
 *     device, deliberately NOT in Firestore (product decision: a reinstall
 *     or new device may show the tutorial again, and that's fine).
 *   • The live tour (active? which step?) — a tiny external store read
 *     through useSyncExternalStore, so the dashboard, profile and the tour
 *     controller all see the same state without a React context.
 */

import { useSyncExternalStore } from "react";

import { logAnalyticsEvent } from "@/lib/analytics";
import { clearDemo, resetDemo } from "@/lib/demo/demo-store";
import type { DemoOwner } from "@/lib/demo/demo-trip";

const FLAG_PREFIX = "onboarding:v1:";

export function isOnboardingDone(uid: string): boolean {
  try {
    const flag = window.localStorage.getItem(FLAG_PREFIX + uid);
    // "existing-user" was written by an earlier build that skipped accounts
    // with trips; it isn't a real "done", so those accounts get the tour too.
    return flag !== null && flag !== "existing-user";
  } catch {
    // Storage blocked (private mode) — treat as done so we don't loop the
    // tour on every visit. Replay from Profile still works.
    return true;
  }
}

export function markOnboardingDone(uid: string, outcome: "completed" | "skipped"): void {
  try {
    window.localStorage.setItem(FLAG_PREFIX + uid, outcome);
  } catch {
    /* ignore */
  }
}

// ─── Live tour store ─────────────────────────────────────────

export interface TourState {
  active: boolean;
  stepIndex: number;
  /** UID the tour was started for — its done-flag is written on finish. */
  uid: string | null;
}

let state: TourState = { active: false, stepIndex: 0, uid: null };
const listeners = new Set<() => void>();

function setState(next: TourState) {
  state = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getTourState(): TourState {
  return state;
}

export function useTourState(): TourState {
  return useSyncExternalStore(subscribe, getTourState, getTourState);
}

/** Seed a fresh sample trip and start at step 0. */
export function startTour(owner: DemoOwner, source: "auto" | "replay"): void {
  resetDemo(owner);
  setState({ active: true, stepIndex: 0, uid: owner.uid });
  logAnalyticsEvent("tutorial_start", { source });
}

export function goToStep(stepIndex: number): void {
  if (!state.active) return;
  setState({ ...state, stepIndex });
  logAnalyticsEvent("tutorial_step", { step: stepIndex });
}

/** Finish (or skip) — writes the done flag and drops the sample trip. */
export function endTour(outcome: "completed" | "skipped"): void {
  if (!state.active) return;
  if (state.uid) markOnboardingDone(state.uid, outcome);
  logAnalyticsEvent(outcome === "completed" ? "tutorial_complete" : "tutorial_skip", {
    step: state.stepIndex,
  });
  setState({ active: false, stepIndex: 0, uid: null });
  clearDemo();
}

/**
 * Drop an in-progress tour without writing the done flag — used when the
 * user signs out mid-tour, so the next account on the device starts clean.
 */
export function abandonTour(): void {
  if (!state.active) return;
  setState({ active: false, stepIndex: 0, uid: null });
  clearDemo();
}
